/**
 * DxfPreviewStage — the hero CAD preview surface for the Tecnico hub.
 *
 * SOTA redesign (June 2026): the mechanical drawing IS the page. Previous
 * layout squeezed the preview into a 320-420px right column; this
 * component owns the full visual real estate and surfaces actions as a
 * floating overlay, never crowding the drawing itself.
 *
 * Two states:
 *
 *   • ``file`` populated  → black canvas with the BE-rendered PNG OR the
 *     interactive viewer (toggle in top-right). Mode toggle, BOM export,
 *     and actions kebab float as a glass-blurred bar at the top of the
 *     stage. File metadata (name, ext, size, status, category) lives as
 *     a thin strip just below the canvas — close enough to belong, far
 *     enough not to crowd.
 *
 *   • ``file`` null  → hero empty state pitched at the engineering
 *     audience: iconography + count + 3-action quick-start.
 *
 * Accessibility:
 *   - Floating action bar is reachable via Tab from the canvas frame.
 *   - Mode toggle is a proper ARIA tablist.
 *   - The drawing has a programmatic name (filename) via ``aria-label``.
 *
 * The component does NOT own the revisions drawer — that lives in a
 * sibling so it can be collapsed independently and reused on mobile.
 */

import { type DxfFile, absoluteApiUrl } from "@ando/core";
import { CadViewerSwitch, cn } from "@ando/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { Compass, ImageIcon, ImageOff, Maximize2, Minimize2, Move, Shirt } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  cadFormatBadgeFor,
  extOf,
  formatBytes,
  isFashionFormat,
  resolveCadFormat,
  statusMetaFor,
} from "../utils";
import { DxfActionsMenu } from "./DxfActionsMenu";
import { DxfBomButton } from "./DxfBomButton";
import { PdfDrawingSheet } from "./PdfDrawingSheet";

/**
 * Shared "panel surface" class — used by the stage outer chrome AND by
 * the details drawer + left rail (technical.tsx + DxfLeftRail) so the
 * duplicated recipe lives in one place. June 2026 DRY audit fix.
 *
 * Jul 2026 pencil pass: translucent warm paper with a shadow-as-hairline
 * contour (mirrors ``PencilCard``) instead of the chunky ``rounded-2xl``
 * + explicit border + cool drop-shadow. Lighter, warmer, less boxed.
 */
export const PANEL_SURFACE_CLASS =
  "rounded-xl bg-white/45 shadow-[0_0_0_1px_rgba(60,40,20,0.07),0_1px_2px_rgba(60,40,20,0.05)]";

/**
 * Tailwind-friendly canvas-dark token. Engineering "drafting table"
 * color — intentionally separate from the semantic palette because the
 * drawing surface must NEVER theme-shift (it's the reference colour
 * white lines render against, used in every CAD app from AutoCAD to
 * Onshape). Centralised here so the maximize-overlay matches the inline
 * canvas exactly.
 */
const CANVAS_DARK_BG = "bg-[#0F1115]";
const CANVAS_DARK_RING_OFFSET = "focus-visible:ring-offset-[#0F1115]";

export interface DxfPreviewStageProps {
  readonly file: DxfFile | null;
  readonly onDeleted?: () => void;
  readonly emptyState: React.ReactNode;
  readonly className?: string;
}

export function DxfPreviewStage({ file, onDeleted, emptyState, className }: DxfPreviewStageProps) {
  if (!file) {
    // Empty state owns the full stage. Sized to match the populated
    // canvas aspect so first selection doesn't cause a layout shift.
    return (
      <div
        className={cn(
          "relative flex w-full flex-col overflow-hidden",
          PANEL_SURFACE_CLASS,
          className,
        )}
      >
        {emptyState}
      </div>
    );
  }

  return (
    <div className={cn("flex w-full flex-col overflow-hidden", PANEL_SURFACE_CLASS, className)}>
      <PreviewCanvas file={file} onDeleted={onDeleted} />
      <FileMetadataStrip file={file} />
    </div>
  );
}
DxfPreviewStage.displayName = "DxfPreviewStage";

// ─────────────────────────────────────────────────────────────────────
// PreviewCanvas — the actual drawing surface
// ─────────────────────────────────────────────────────────────────────

function PreviewCanvas({
  file,
  onDeleted,
}: {
  file: DxfFile;
  onDeleted?: () => void;
}) {
  const { t } = useLingui();
  // ``interactive``: WebGL viewer with pan/zoom (matches V1's UX —
  // user feedback Jun 8, 2026 "non riesco a fare zoom"). ``static`` is
  // the fallback when the DXF binary is unavailable (download_url
  // missing → CadViewerSwitch has nothing to render).
  //
  // We pick the initial mode reactively from ``hasInteractive`` so the
  // panel doesn't start in the wrong mode while the explore query
  // hydrates ``download_url`` lazily. The useEffect below switches to
  // interactive the moment the URL lands.
  // ``viewer_url`` (Jun 17, 2026) is the URL the 3D viewer parses
  // client-side. For STEP/IGES it points at the BE-tessellated STL
  // (StepViewer uses three-stdlib's STLLoader); for STL files it
  // mirrors download_url; for DXF the 2D viewer can read the source
  // directly, so we still fall back to ``download_url``. Older BE
  // releases didn't emit ``viewer_url`` — the fallback keeps the
  // viewer alive while clients ride a mixed-version fleet.
  //
  // Jun 18, 2026 — wrap in ``apiUrl()`` to convert the BE-supplied
  // relative path (e.g. ``/api/v1/documents/minio/file/...``) into a
  // fully-qualified URL against the configured API host. The previous
  // bare relative URL got resolved by the browser against the page
  // origin (``beta-web.ando-ai.com``) instead of the API host
  // (``api.ando-ai.com``); ``beta-web`` has no ``/api/*`` proxy and
  // falls back to the SPA's ``index.html`` for every unmatched path,
  // so ``StepViewer`` was actually parsing 4 KB of HTML as if it were
  // a binary STL. The header's uint32 at offset 80 (``<!doc``) maps
  // to ~1.7 billion triangles → ``RangeError: Array buffer`` OOM.
  // Local dev wasn't affected because Vite's dev-server proxy
  // forwards ``/api/*`` to the BE on the same origin.
  // Fashion-axis files (garment mesh / CLO project / apparel pattern) have
  // NO interactive viewer: OBJ/glTF/GLB/FBX and the proprietary CLO
  // container aren't parseable by the DXF/STEP/STL ``CadViewerSwitch``
  // (it would render "formato non riconosciuto"). The BE-rendered
  // contact-sheet montage PNG (``preview_url``) IS the preview, so we
  // force the static branch and drop the Naviga/Anteprima toggle. Gating
  // the URL to ``null`` here is the single lever — everything downstream
  // (``hasInteractive``, the toggle, the initial mode) reads off it, so
  // the mechanical path stays byte-identical.
  const resolvedFormat = resolveCadFormat(file);
  const isFashion = isFashionFormat(resolvedFormat);
  // PDF technical drawings (Sep 2, 2026): ``CadViewerSwitch`` can't parse
  // PDF bytes, but the tavola IS navigable — ``PdfDrawingSheet`` mounts the
  // production react-pdf viewer (zoom, pan-scroll, page nav, Fit) on the
  // auth-proxied blob, same as the Hive-flow run pane. The interactive URL
  // stays null (nothing for the WebGL viewer to load); ``hasInteractive``
  // is forced on so the Naviga/Anteprima toggle + default mode behave
  // exactly like the DXF path.
  const isPdf = resolvedFormat === "pdf";
  const staticOnly = isFashion || isPdf;
  const rawInteractiveUrl = staticOnly
    ? null
    : ((file.viewer_url as string | null | undefined) ?? file.download_url ?? null);
  const interactiveUrl = rawInteractiveUrl ? absoluteApiUrl(rawInteractiveUrl) : null;
  const hasInteractive = Boolean(interactiveUrl) || isPdf;
  const [mode, setMode] = useState<"static" | "interactive">(
    hasInteractive ? "interactive" : "static",
  );
  const [maximized, setMaximized] = useState(false);
  // The proxy URL can still fail (MinIO 404 / network drop / signature
  // mismatch on the legacy path). Track the load outcome so a broken
  // img doesn't fall through to the browser's broken-icon — show our
  // typed unavailable state instead.
  const [imgFailed, setImgFailed] = useState(false);
  // Same cross-origin fix (Jun 18, 2026) — see ``rawInteractiveUrl``.
  // The static-image branch was hitting the same SPA-fallback trap on
  // beta-web; ``<img src>`` then rendered the broken-image icon and the
  // user fell through to the "no_preview" empty state.
  const previewUrl = file.preview_url ? absoluteApiUrl(file.preview_url) : null;
  // Reset failed flag whenever the URL changes (new selection).
  useEffect(() => {
    setImgFailed(false);
  }, [previewUrl]);
  // Hydrate interactive mode as soon as ``download_url`` lands. The
  // explore query is async; when the user lands on a row before its
  // ``download_url`` is fetched we'd otherwise stay stuck in static
  // mode until they click the toggle. The opposite transition
  // (interactive → static when download_url disappears) is unusual
  // but handled symmetrically for completeness.
  useEffect(() => {
    setMode(hasInteractive ? "interactive" : "static");
  }, [hasInteractive]);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Maximize: Esc close + focus restore + body scroll lock. WCAG 2.1.2
  // (no keyboard trap) + 2.4.3 (focus order) compliance.
  useEffect(() => {
    if (!maximized) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    overlayRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setMaximized(false);
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [maximized]);

  return (
    <div
      ref={overlayRef}
      tabIndex={maximized ? -1 : undefined}
      role={maximized ? "dialog" : undefined}
      aria-modal={maximized ? true : undefined}
      aria-label={maximized ? t`Anteprima a tutto schermo: ${file.filename}` : undefined}
      className={cn(
        // Engineering "drafting table" canvas with subtle 24x24 grid.
        // The grid + radial vignette suggest scale without competing
        // with the drawing itself.
        "relative w-full overflow-hidden",
        CANVAS_DARK_BG,
        maximized
          ? "fixed inset-0 z-50 rounded-none focus:outline-none"
          : "aspect-[16/10] min-h-[520px] rounded-t-xl",
      )}
      style={{
        backgroundImage:
          "radial-gradient(ellipse at center, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 70%), linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "100% 100%, 24px 24px, 24px 24px",
        backgroundPosition: "center, 0 0, 0 0",
      }}
    >
      {/* Drawing — static preview PNG, navigable PDF sheet, or WebGL viewer */}
      {mode === "interactive" && isPdf ? (
        <PdfDrawingSheet file={file} />
      ) : mode === "interactive" && hasInteractive ? (
        // ``fileSize`` is intentionally omitted — V1's DXF library
        // doesn't enforce the 5 MB low-RAM guard and users routinely
        // navigate 10–20 MB drawings on desktop. The guard exists for
        // the chat-attachment surface where mobile is a first-class
        // target; in the Tecnico hub we trust the desktop floor.
        <CadViewerSwitch
          fileUrl={interactiveUrl}
          filename={file.filename}
          className="absolute inset-0 h-full w-full"
        />
      ) : previewUrl && !imgFailed ? (
        // White-paper inset (Jun 17, 2026) — the HLR PNGs OCCT writes
        // are dark line-art on TRANSPARENT background; rendering them
        // directly on the dark drafting-table canvas hides the lines
        // entirely. We wrap the image in a slightly inset white sheet
        // so the drawing reads as paper on the desk — matching every
        // CAD viewer convention (Onshape, Inventor View, eDrawings).
        // The dark canvas still shows around the sheet as framing.
        <div className="absolute inset-3 flex items-center justify-center rounded-md bg-white p-3 shadow-2xl ring-1 ring-black/10 sm:inset-6 sm:p-6">
          <img
            src={previewUrl}
            alt={t`Anteprima ${file.filename}`}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            onError={(e) => {
              // The presigned URL was rendered but the network fetch
              // failed (404 / CORS / signature mismatch / MinIO down).
              // Log the URL + naturalSize for ops diagnostics so we can
              // tell signature failures (HTTP 403) from missing-file
              // (HTTP 404) from CORS-block (no response).
              const img = e.currentTarget;
              console.warn("[DxfPreviewStage] preview image failed to load", {
                url: previewUrl,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
                filename: file.filename,
              });
              setImgFailed(true);
            }}
          />
        </div>
      ) : (
        // Four sub-states based on BE ingestion_status + whether the
        // image actually loaded. The user needs to know WHY the
        // preview is empty, not just THAT it is.
        //   pending/processing → analyzer still running
        //   failed             → analyzer failed
        //   imgFailed=true     → URL was set but the image fetch broke
        //                        (404/CORS/signature) — most actionable
        //                        sub-state: tell the user something
        //                        upstream needs ops attention.
        //   else               → analyzer succeeded but skipped preview.
        <PreviewUnavailable
          file={file}
          urlAttemptFailed={imgFailed && Boolean(previewUrl)}
          onSwitchTo3D={hasInteractive ? () => setMode("interactive") : undefined}
          onRetry={
            imgFailed
              ? () => setImgFailed(false) // re-render the <img>
              : undefined
          }
        />
      )}

      {/* Floating action bar — top-right corner, glass-blurred so the drawing
          stays the visual hero. Tab order: viewer toggle → BOM export →
          maximise → kebab. */}
      <div className="pointer-events-none absolute top-3 right-3 left-3 flex items-start justify-between gap-2">
        {/* Left side: viewer mode toggle. role="group" (not tablist) —
            we don't render a separate tabpanel so toggle-group is the
            honest contract; click + arrow-keys handled natively by the
            two buttons inside. */}
        {hasInteractive ? (
          <div
            role="group"
            aria-label={t`Modalità visualizzazione`}
            className="pointer-events-auto inline-flex gap-0.5 rounded-lg bg-black/40 p-0.5 backdrop-blur-md"
          >
            {/* "Naviga" (drag + scroll-to-zoom WebGL) and "Anteprima"
                (static PNG) — renamed from the old "3D" label which
                clashed with the unrelated "Estrudi 3D" feature in the
                drawer (Jun 8 user feedback). Naviga is the default
                when ``hasInteractive`` so V1's pan/zoom UX is the
                immediate, no-click experience. */}
            <ViewerToggleButton
              active={mode === "interactive"}
              onClick={() => setMode("interactive")}
              icon={<Move size={12} strokeWidth={1.75} />}
              label={t`Naviga`}
            />
            <ViewerToggleButton
              active={mode === "static"}
              onClick={() => setMode("static")}
              icon={<ImageIcon size={12} strokeWidth={1.75} />}
              label={t`Anteprima`}
            />
          </div>
        ) : (
          <div aria-hidden />
        )}

        {/* Right side: BOM + maximize + kebab. BOM (bill-of-materials from
            block components) is a mechanical-drawing action — a garment
            mesh / CLO project has no extractable blocks, so we hide it for
            the fashion axis rather than surface a button that always 422s. */}
        <div className="pointer-events-auto flex items-center gap-1.5">
          {/* BOM extracts DXF block components — a fashion mesh has none and
              a PDF tavola has no parseable blocks either: hide instead of
              surfacing a button that always 422s. */}
          {isFashion || isPdf ? null : <DxfBomButton file={file} variant="primary" />}
          <button
            type="button"
            onClick={() => setMaximized((m) => !m)}
            aria-label={maximized ? t`Riduci anteprima` : t`Massimizza anteprima`}
            aria-pressed={maximized}
            className={cn(
              "inline-flex h-8 w-8 items-center justify-center rounded-lg bg-black/40 text-white/80 backdrop-blur-md transition-colors hover:bg-black/55 hover:text-white motion-reduce:transition-none",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2",
              CANVAS_DARK_RING_OFFSET,
            )}
          >
            {maximized ? (
              <Minimize2 size={14} strokeWidth={1.75} aria-hidden />
            ) : (
              <Maximize2 size={14} strokeWidth={1.75} aria-hidden />
            )}
          </button>
          <div className="rounded-lg bg-black/40 backdrop-blur-md">
            <DxfActionsMenu file={file} onDeleted={onDeleted} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FileMetadataStrip — minimal strip under the canvas with file context
// ─────────────────────────────────────────────────────────────────────

function FileMetadataStrip({ file }: { file: DxfFile }) {
  const { i18n } = useLingui();
  const status = statusMetaFor(i18n, file);
  const format = resolveCadFormat(file);
  const formatBadge = cadFormatBadgeFor(i18n, format);
  const isFashion = formatBadge !== null;

  return (
    <div className="flex items-center gap-3 border-hairline border-t bg-card/60 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ando-orange-soft text-ando-orange">
        {isFashion ? (
          <Shirt size={16} strokeWidth={1.75} aria-hidden />
        ) : (
          <Compass size={16} strokeWidth={1.75} aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate font-semibold text-ink-1 text-sm">{file.filename}</h3>
        <p className="mt-0.5 truncate text-[11px] text-ink-3 tabular-nums">
          <span>{extOf(file.filename)}</span>
          <span aria-hidden> · </span>
          <span>{formatBytes(file.file_size)}</span>
          {/* CLO/Marvelous provenance clarifier — a ``fashion-project`` is
              a proprietary ``.zprj`` / ``.zpac`` container, so we name the
              authoring tool alongside the friendly "Progetto moda" badge. */}
          {format === "fashion-project" ? (
            <>
              <span aria-hidden> · </span>
              <span>
                <Trans>CLO/Marvelous</Trans>
              </span>
            </>
          ) : null}
          {file.category ? (
            <>
              <span aria-hidden> · </span>
              <span>{file.category}</span>
            </>
          ) : null}
        </p>
      </div>
      {formatBadge ? (
        <span
          className={cn(
            "shrink-0 rounded-md px-1.5 py-0.5 font-medium text-[10px]",
            formatBadge.className,
          )}
        >
          {formatBadge.label}
        </span>
      ) : null}
      <span
        className={cn("shrink-0 rounded-md px-2 py-0.5 font-medium text-[10px]", status.className)}
      >
        {status.label}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// PreviewUnavailable — diagnostic state when ``preview_url`` is null
// ─────────────────────────────────────────────────────────────────────

function PreviewUnavailable({
  file,
  urlAttemptFailed = false,
  onSwitchTo3D,
  onRetry,
}: {
  file: DxfFile;
  urlAttemptFailed?: boolean;
  onSwitchTo3D?: () => void;
  onRetry?: () => void;
}) {
  const { t } = useLingui();
  const status = file.status;
  const isProcessing = status === "pending" || status === "processing";
  const isFailed = status === "failed";
  // The "URL set but image failed" branch wins over status-based
  // messaging — it's the most actionable signal we have. The user
  // sees "Errore caricamento" + a Retry that tries the fetch again
  // (handles transient signature races + stale cache).

  const variant: "url_failed" | "processing" | "failed" | "no_preview" = urlAttemptFailed
    ? "url_failed"
    : isProcessing
      ? "processing"
      : isFailed
        ? "failed"
        : "no_preview";

  const title = {
    url_failed: t`Errore di caricamento anteprima`,
    processing: t`Anteprima in generazione…`,
    failed: t`Anteprima non generata`,
    no_preview: t`Anteprima non disponibile`,
  }[variant];

  const body = {
    url_failed: (
      <Trans>
        L'URL dell'anteprima è stato emesso dal server ma il browser non è riuscito a scaricarla
        (rete, CORS o file rimosso). Puoi riprovare oppure passare al viewer 3D.
      </Trans>
    ),
    processing: (
      <Trans>
        Stiamo renderizzando il PNG del disegno. Aggiorna fra qualche secondo o passa al viewer 3D
        per esplorare subito.
      </Trans>
    ),
    failed: (
      <Trans>
        Il render automatico non è riuscito per questo file. Usa il viewer 3D per visualizzarlo o
        scaricalo per aprirlo in CAD.
      </Trans>
    ),
    no_preview: (
      <Trans>
        Il file è indicizzato ma non è stata generata un'anteprima PNG (entità non supportate o
        dimensione oltre soglia).
      </Trans>
    ),
  }[variant];

  return (
    <div
      role="img"
      aria-label={title}
      className="absolute inset-0 flex items-center justify-center px-6 text-center"
    >
      <div className="flex max-w-md flex-col items-center gap-3 text-white/70">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/[0.06]">
          {variant === "processing" ? (
            <Compass
              size={26}
              strokeWidth={1.25}
              aria-hidden
              className="motion-safe:animate-spin"
              style={{ animationDuration: "3s" }}
            />
          ) : (
            <ImageOff size={26} strokeWidth={1.25} aria-hidden />
          )}
        </div>
        <div className="space-y-1">
          <p className="font-semibold text-[13px] text-white">{title}</p>
          <p className="text-[11px] text-white/60 leading-relaxed">{body}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg bg-ando-orange px-3 py-1.5 font-medium text-[11px] text-white transition-colors hover:bg-ando-orange/90 motion-reduce:transition-none",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2",
                CANVAS_DARK_RING_OFFSET,
              )}
            >
              <Compass size={12} strokeWidth={1.75} aria-hidden />
              <Trans>Riprova</Trans>
            </button>
          ) : null}
          {onSwitchTo3D ? (
            <button
              type="button"
              onClick={onSwitchTo3D}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 font-medium text-[11px] text-white transition-colors hover:bg-white/15 motion-reduce:transition-none",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2",
                CANVAS_DARK_RING_OFFSET,
              )}
            >
              <Move size={12} strokeWidth={1.75} aria-hidden />
              <Trans>Apri viewer navigabile</Trans>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ViewerToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  // ARIA-pressed (not aria-selected) because the parent is role="group"
  // not "tablist". Honest semantics — keyboard arrow-nav is not wired,
  // tab-key cycles through the two buttons natively which matches the
  // user expectation for a toggle group.
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-[11px] transition-colors motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2",
        CANVAS_DARK_RING_OFFSET,
        active ? "bg-white text-ink-1" : "text-white/70 hover:bg-white/10 hover:text-white",
      )}
    >
      <span aria-hidden>{icon}</span>
      {label}
    </button>
  );
}
